import { eq } from "@-/com.npmjs.drizzle-orm";
import ensureError from "@-/com.npmjs.ensure-error";
import type { SlideManifestEntry } from "@-/dev.improve.haniverse.assets.slides";
import type { GraphemeTaiwanUsageMap } from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import type { ContentDatabase } from "@-/dev.improve.haniverse.database.content";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import {
  createUserCard,
  createUserPronunciationCards,
} from "@-/dev.improve.haniverse.lib.cards.user-card.create";
import { buildUnitSequence, type SequenceEntry } from "@-/dev.improve.haniverse.lib.lesson.get";
import { ensureUserSlideRow } from "@-/dev.improve.haniverse.lib.slide.row";
import type { Level } from "@-/dev.improve.haniverse.lib.level";

export type EnrollLessonsBeforeLevelResult = {
  /** Character and pronunciation lessons enrolled as studied. */
  enrolledCount: number;
  /**
   * Unpinned pronunciation lessons with no eligible reading to enroll. Glyph meanings always
   * exist, and a lesson pinned to one reading must enroll it.
   */
  skippedCount: number;
  /** Character and pronunciation lessons before the level. */
  totalCount: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;
/** Seeded reviews fall due across this many days, so the new position starts with a calm queue. */
const REVIEW_SPREAD_DAYS = 30;

// Every glyph has a meaning card. Only a pronunciation lesson can have nothing to enroll.
const NOTHING_TO_ENROLL_ERROR_PREFIXES = ["No pronunciation card to enroll"];

/**
 * Puts the learner at the start of `levels[levelIndex]`: every lesson before it counts as done and
 * every slide from that level on is unseen again.
 *
 * Lessons before the level are replayed in curriculum order through the production enrollment
 * path — {@link createUserCard} for each character, {@link createUserPronunciationCards} for each
 * pronunciation-level lesson — so the deck is the one a learner who walked there would hold
 * (a meaning-only level's characters get their readings only once their pronunciation level is
 * behind the learner). Their slides are marked seen, keeping any favorite.
 *
 * Seeded cards were introduced yesterday, so they do not count against today's new-character
 * allowance, and fall due over the next {@link REVIEW_SPREAD_DAYS} days.
 *
 * Enrollment only adds cards: start from an empty deck (`useClearActorDeck`) so nothing at or past
 * the level stays studied.
 */
export async function enrollLessonsBeforeLevel({
  actorDb,
  contentDb,
  hanziScript,
  levelIndex,
  levels,
  now,
  onProgress,
  signal,
  slideManifest,
  twUsageMap,
}: {
  actorDb: ActorDatabase;
  contentDb: ContentDatabase;
  hanziScript: HanziScript;
  /** `Level.index` of the level to start at. */
  levelIndex: number;
  levels: readonly Level[];
  now: Date;
  /** Called after each lesson with how many of `total` are done. */
  onProgress?: (done: number, total: number) => void;
  /** Stops the walk between lessons once aborted. */
  signal?: AbortSignal;
  /** Defaults to the bundled slide manifest. */
  slideManifest?: readonly SlideManifestEntry[];
  twUsageMap: GraphemeTaiwanUsageMap;
}): Promise<EnrollLessonsBeforeLevelResult> {
  const sequence = buildUnitSequence(levels, slideManifest);
  const lessons = sequence.filter(
    (entry): entry is Extract<SequenceEntry, { type: "morpheme" }> =>
      entry.type === "morpheme" && entry.level.index < levelIndex,
  );
  const startedStudyingAt = new Date(now.getTime() - DAY_MS).toISOString();

  let enrolledCount = 0;
  let skippedCount = 0;
  // Sequential and in curriculum order: each enrollment opens its own transaction, and a
  // pronunciation lesson must land after the meaning card its character's level enrolled.
  for (const [lessonIndex, lesson] of lessons.entries()) {
    if (signal?.aborted === true) {
      throw new Error("Setting the current level was cancelled");
    }
    const scheduledDays = 1 + (lessonIndex % REVIEW_SPREAD_DAYS);
    const args = {
      actorDb,
      contentDb,
      graphemeCard: { front: lesson.character },
      hanziScript,
      initialFsrsSchedule: {
        due: new Date(now.getTime() + scheduledDays * DAY_MS).toISOString(),
        scheduledDays,
      },
      startedStudyingAt,
      twUsageMap,
    };
    try {
      await (lesson.part === "pronunciation"
        ? createUserPronunciationCards({ ...args, onlyPinyin: lesson.pinyin })
        : createUserCard({ ...args, part: lesson.part }));
      enrolledCount += 1;
    } catch (error) {
      const ensured = ensureError(error);
      // A lesson pinned to one reading (干 gan4 on 1-P1) is learned only once that exact reading
      // is in study. Skipping it would leave it the learner's next lesson, failing on every swipe,
      // so a pinned reading with nothing to enroll is a content defect and fails the walk.
      if (
        lesson.pinyin !== undefined ||
        !NOTHING_TO_ENROLL_ERROR_PREFIXES.some((prefix) => ensured.message.startsWith(prefix))
      ) {
        throw ensured;
      }
      skippedCount += 1;
    }
    onProgress?.(lessonIndex + 1, lessons.length);
  }

  await markSlidesSeenBeforeLevel({
    actorDb,
    levelIndex,
    now,
    // A slide's sequence level is its grapheme `Level.index` plus one (see `getLessonLevelIndex`).
    slides: sequence.flatMap((entry) =>
      entry.type === "slide" ? [{ key: entry.key, levelIndex: entry.sortKey[0] - 1 }] : [],
    ),
  });

  return { enrolledCount, skippedCount, totalCount: lessons.length };
}

/**
 * Slides before the level are seen; slides from it on are unseen. Only `completed_at` changes, so
 * favorites survive in both directions.
 */
async function markSlidesSeenBeforeLevel({
  actorDb,
  levelIndex,
  now,
  slides,
}: {
  actorDb: ActorDatabase;
  levelIndex: number;
  now: Date;
  slides: readonly { key: string; levelIndex: number }[];
}): Promise<void> {
  const nowIso = now.toISOString();
  await actorDb(import.meta.location).transaction(async (tx) => {
    const rows = await tx.query.userSlide.findMany({
      columns: { completedAt: true, id: true, key: true },
    });
    const rowByKey = new Map(rows.map((row) => [row.key, row]));
    for (const slide of slides) {
      const row = rowByKey.get(slide.key);
      if (slide.levelIndex >= levelIndex) {
        if (row !== undefined && row.completedAt !== null) {
          await tx
            .update(actorSchema.userSlide)
            .set({ completedAt: null })
            .where(eq(actorSchema.userSlide.id, row.id));
        }
        continue;
      }
      if (row === undefined || row.completedAt === null) {
        // A slide with no row gets a blank one first (see `completeSlide`).
        const id = row?.id ?? (await ensureUserSlideRow(tx, slide.key, nowIso));
        await tx
          .update(actorSchema.userSlide)
          .set({ completedAt: nowIso })
          .where(eq(actorSchema.userSlide.id, id));
      }
    }
  });
}
